import { useEffect, useId, useRef, useState } from 'react';

const QUOTES = [
  '"Every sale has five basic obstacles: no need, no money, no hurry, no desire, no trust." — Zig Ziglar',
  '"The secret of getting ahead is getting started." — Mark Twain',
  '"Your most unhappy customers are your greatest source of learning." — Bill Gates',
  '"It\'s not about having the right opportunities. It\'s about handling the opportunities right." — Mark Hunter',
  '"Pipeline is the lifeblood of sales. Always be filling it." — Unknown',
  '"The difference between a successful person and others is not lack of strength, not lack of knowledge, but rather a lack in will." — Vince Lombardi',
  '"You don\'t need a big close. As long as you are solving your customer\'s problems, you will win." — Evan Carmichael',
  '"Make a customer, not a sale." — Katherine Barchetti',
  '"The best salespeople are the ones who truly understand the customer\'s business." — Unknown',
  '"Approach each customer with the idea of helping them solve a problem or achieve a goal." — Brian Tracy',
  '"In sales, it\'s not about selling. It\'s about building trust." — Siva Devaki',
  '"The harder I practice, the luckier I get." — Gary Player',
  '"Confidence is the most important single factor in this game." — Jack Nicklaus',
  '"Success is where preparation and opportunity meet." — Bobby Unser',
  '"Champions aren\'t made in the gyms. Champions are made from something they have deep inside them." — Muhammad Ali',
  '"A champion is someone who gets up when they can\'t." — Jack Dempsey',
  '"The will to win is important, but the will to prepare is vital." — Joe Paterno',
  '"Repetition is the mother of skill." — Tony Robbins',
];

const ROTATION_INTERVAL_MS = 4000;
const FADE_DURATION_MS = 400;

type Variant = 'fullscreen' | 'modal';

interface Props {
  open: boolean;
  variant?: Variant;
  title?: string;
  subtitle?: string;
}

export function LoadingQuotesDialog({
  open,
  variant = 'modal',
  title = 'Loading…',
  subtitle,
}: Props) {
  const titleId = useId();
  const initialIdxRef = useRef(Math.floor(Math.random() * QUOTES.length));
  const [quoteIdx, setQuoteIdx] = useState(initialIdxRef.current);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (!open) return;
    // Reset to fresh random quote each time dialog opens
    const startIdx = Math.floor(Math.random() * QUOTES.length);
    initialIdxRef.current = startIdx;
    setQuoteIdx(startIdx);
    setVisible(true);

    const interval = setInterval(() => {
      // Fade out
      setVisible(false);
      setTimeout(() => {
        setQuoteIdx((prev) => (prev + 1) % QUOTES.length);
        setVisible(true);
      }, FADE_DURATION_MS);
    }, ROTATION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [open]);

  if (!open) return null;

  if (variant === 'fullscreen') {
    return (
      <div
        className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-gray-900 rounded-lg"
        role="status"
        aria-labelledby={titleId}
        aria-live="polite"
        data-testid="loading-quotes-fullscreen"
      >
        <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mb-6" />
        <h2 id={titleId} className="text-gray-100 text-xl font-semibold mb-1 text-center px-8">
          {title}
        </h2>
        {subtitle && <p className="text-gray-400 text-sm mb-8 text-center px-8">{subtitle}</p>}
        <blockquote
          className="max-w-md text-center px-8 text-gray-400 text-sm italic transition-opacity"
          style={{
            opacity: visible ? 1 : 0,
            transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
          }}
        >
          {QUOTES[quoteIdx]}
        </blockquote>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      role="presentation"
      data-testid="loading-quotes-modal"
    >
      <div
        role="status"
        aria-labelledby={titleId}
        aria-live="polite"
        className="bg-white rounded-xl shadow-xl max-w-md w-full p-8 flex flex-col items-center gap-4"
      >
        <div className="w-10 h-10 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
        <div className="text-center">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900">
            {title}
          </h2>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <blockquote
          className="text-center text-sm text-gray-500 italic max-w-xs"
          style={{
            opacity: visible ? 1 : 0,
            transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
          }}
        >
          {QUOTES[quoteIdx]}
        </blockquote>
      </div>
    </div>
  );
}
